import { db } from './firebase';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
} from 'firebase/firestore';

const TIMEOUT_MS = 10000;

/**
 * Promiseにタイムアウトを設定するラッパー
 */
const withTimeout = (promise, ms) => {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Request timeout')), ms)
  );
  return Promise.race([promise, timeout]);
};

/**
 * 指定ラリーのスポット一覧を取得
 *
 * /campaigns/ コレクションは廃止し、/mapRallies/ と /mapSpots/ を参照する。
 *   - /mapRallies/{campaignId}         ラリーメタデータ（updatedAt, gasLastUpdated 等）
 *   - /mapSpots/ where rallyId == id   スポット一覧
 *
 * 戻り値インターフェースはフェーズ0（fetchCampaignSpots）と同一に保ち、
 * useStampRallyData・StampRallyPage への影響を最小化する。
 *
 * @param {string} campaignId - キャンペーンID（例: "ueno"）
 * @returns {Promise<{ spots: Array, updatedAt: Date|null, gasLastUpdated: Date|null }>}
 */
export const fetchCampaignSpots = async (campaignId) => {
  // ── ラリーメタデータ取得（/mapRallies/{campaignId}） ──
  const rallyRef = doc(db, 'mapRallies', campaignId);
  const rallySnap = await withTimeout(getDoc(rallyRef), TIMEOUT_MS);

  let updatedAt = null;
  let gasLastUpdated = null;
  if (rallySnap.exists()) {
    const data = rallySnap.data();
    updatedAt = data.updatedAt?.toDate?.() || null;
    // GASが集計した時刻（ISO文字列 → Date）
    gasLastUpdated = data.gasLastUpdated ? new Date(data.gasLastUpdated) : null;
  }

  // ── スポット一覧取得（/mapSpots/ where rallyId == campaignId） ──
  const spotsQuery = query(
    collection(db, 'mapSpots'),
    where('rallyId', '==', campaignId)
  );
  const spotsSnap = await withTimeout(getDocs(spotsQuery), TIMEOUT_MS);

  const spots = spotsSnap.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      // ドキュメントID は "{campaignId}_{spotId}" 形式なので数値部分だけ id として返す
      id: String(docSnap.id).replace(`${campaignId}_`, ''),
      name: data.name,
      stampCount: data.stampCount,
      freeCount: data.freeCount ?? 0,
      paidCount: data.paidCount ?? 0,
      lat: data.lat ?? null,
      lng: data.lng ?? null,
    };
  });

  // id でソート（数値として）
  spots.sort((a, b) => Number(a.id) - Number(b.id));

  return { spots, updatedAt, gasLastUpdated };
};

/**
 * appConfig/version からデプロイ日時を取得
 *
 * Firestore:
 *   appConfig (collection)
 *     └─ version (document)
 *          ├─ deployedAt: string  (ISO 8601形式, 例: "2026-06-17T10:00:00.000Z")
 *          └─ updatedAt: Timestamp
 *
 * @returns {Promise<{ deployedAt: string | null }>}
 */
export const fetchAppVersion = async () => {
  const versionRef = doc(db, 'appConfig', 'version');
  const snap = await withTimeout(getDoc(versionRef), TIMEOUT_MS);
  if (!snap.exists()) return { deployedAt: null };
  return { deployedAt: snap.data().deployedAt ?? null };
};
